import { property, remote, supertypeClass } from '@haventech/amorphic';
import { Controller } from '../Controller';
import { AccountCenterControllerInterface } from '../../../../coverpath-common/angular/interfaces/account-center/AccountCenterControllerInterface';
import { Utils } from '../../../../common/js/Utils';
import { PaymentInformation } from '../../../../coverpath-common/classes/PaymentInformation';
import { AMCController } from '../../../../common/js/authentication/amc/AMCController';
import { PolicyAdminClient } from '../../../../common/js/policyAdmin/PolicyAdminClient';
import { InitialInvoice } from '../../../../common/js/policyAdmin/interface/InitialInvoiceInfoResponse';
import { CoverpathAnnuityApplication } from '../../../../coverpath-common/classes/coverpath_subclasses/CoverpathAnnuityApplication';
import { CoverpathCustomer } from '../../../../coverpath-common/classes/coverpath_subclasses/CoverpathCustomer';
import { CoverpathPolicy } from '../../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { CasApplication } from '../../../../coverpath-common/classes/coverpath_subclasses/CasApplication';
import { CoverpathBaseAccountCenterController } from '../../../../coverpath-common/js/controllers/CoverpathBaseAccountCenterController';
import { LoggerSingleton, Category } from '../../../../common/js/logger/LoggerSingleton';
import { CdmService, AgencyCorpsRelationship } from '../../../../common/js/services/CdmService';
import { ClientReapplyResponseType } from '../../../../coverpath-common/js/interfaces/CoverpathTypes';

@supertypeClass({ toClient: true, toServer: true })
export class AccountCenterController
  extends CoverpathBaseAccountCenterController
  implements AccountCenterControllerInterface
{
  @property({ getType: () => Controller })
  controller: Controller;

  constructor() {
    super();
    this.paymentInformation = PaymentInformation.new();
  }

  static new(parentController): AccountCenterController {
    return new AccountCenterController().init(parentController);
  }

  init(parentController): this {
    this.controller = parentController;
    return this;
  }

  getPath(): string {
    return 'coverpath-client';
  }

  getController(): Controller {
    return this.controller;
  }

  getAMCController(): AMCController {
    return this.controller.getAMCController();
  }

  getCustomer(): CoverpathCustomer {
    return this.controller.customer;
  }

  getApplication(): CasApplication {
    return this.controller.customer.application;
  }

  getLastestApplication(): CasApplication {
    return this.controller.customer.getLatestApplication();
  }

  getAppLevelAmorphicTransaction() {
    return this.controller.txn;
  }

  @remote({ on: 'server' })
  async acceptAmendedPolicy(policyNumber: string) {
    LoggerSingleton.getInstance().info(
      {
        module: 'AccountCenterController',
        function: 'acceptAmendedPolicy',
        category: Category.milestone,
        context: {
          referenceType: 'PolicyNumber',
          referenceId: policyNumber,
        },
      },
      'Customer accepted amendments'
    );

    try {
      await this.getAndSignForm(policyNumber, 'Amendment');

      LoggerSingleton.getInstance().info(
        {
          module: 'AccountCenterController',
          function: 'acceptAmendedPolicy',
          category: Category.milestone,
          context: {
            referenceType: 'PolicyNumber',
            referenceId: policyNumber,
          },
        },
        `Copying amendment values onto Policy: updatePolicyWithAmendmentValues`
      );
    } catch (e) {
      LoggerSingleton.getInstance().error(
        {
          module: 'accountCenterController',
          function: 'acceptAmendedPolicy',
          category: 'milestone',
          context: {
            referenceType: 'PolicyNumber',
            referenceId: policyNumber,
          },
          error: e,
        },
        `There was an error in accepting amendments`
      );

      //reset the in-memory objects
      await this.controller.customer.refresh();
      await this.controller.customer.enrichCustomerOptimized();

      throw e;
    }
  }

  @remote({ on: 'server' })
  async getInitialInvoiceInfo(policyNumber: string): Promise<InitialInvoice> {
    let initialInvoice: InitialInvoice;
    initialInvoice = { amount: 0, coverageStartDate: null, coverageEndDate: null };
    try {
      const policy = this.getPolicyWithNumber(policyNumber);
      initialInvoice = await PolicyAdminClient.getInitialInvoiceInfo(policy as CoverpathPolicy, undefined, undefined);
    } catch (e) {
      const err = {
        name: e?.name,
        message: e?.message,
        stack: e?.stack,
        data: {
          policyNumber: policyNumber,
        },
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'accountCenterController',
          function: 'getInitialInvoiceInfo',
          category: Category.request,
          context: {
            referenceType: 'PolicyNumber',
            referenceId: policyNumber,
          },
          error: err,
        },
        `Call to Invoice PA endpoint threw exception, returning 0 invoice amount`
      );
      return initialInvoice;
    }
    return initialInvoice;
  }

  @remote({ on: 'server' })
  async esignAnnuity() {
    await (this.getApplication() as CoverpathAnnuityApplication).annuity.fulfillRequirements([
      `CA1R2 Annuity Application signed`,
    ]);

    this.getApplication().primaryPolicy.applicationSignedDate = Utils.now();
    this.getApplication().primaryPolicy.applicationSignedState =
      this.getApplication().primaryPolicy.owner.legalAddress.state;
  }

  @remote({ on: 'server' })
  async acceptAnnuityOffer() {
    const application = this.getApplication() as CoverpathAnnuityApplication;
    application.finalOfferAcceptedDate = Utils.now();
    await application.annuity.fulfillRequirements([`AA1R1 Annuity issue has been confirmed`]);
  }

  @remote({ on: 'server' })
  async cloneApplicationForReapply(): Promise<boolean> {
    const success: boolean = await super.cloneApplicationForReapply();
    if (success) {
      this.getCustomer().registerPostServerCallCallback(() => this.controller.handlePiiAfterApplicationClone());
    }
    return success;
  }

  @remote({ on: 'server' })
  async reapplyApplication(): Promise<ClientReapplyResponseType> {
    const originalApplication = this.getApplication();
    const originalPrimaryPolicy: CoverpathPolicy = originalApplication.primaryPolicy;
    const originalPrimaryPolicyNumber = originalApplication.primaryPolicy.policyNumber;
    const cdmServiceInstance = new CdmService();

    // Use getProducerInfo() to check whether primary/servicing advisor is active
    try {
      await cdmServiceInstance.getProducerInfo(originalPrimaryPolicy.primaryPolicyAgent.agent.producerId);
    } catch (err) {
      // If primary/servicing advisor is inactive, return enum type INACTIVE_PRIMARY_ADVISOR.
      LoggerSingleton.getInstance().error(
        {
          module: 'AccountCenterController',
          function: 'getProducerAgencyCorpFromCdm',
          category: Category.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
            data: {
              primaryPolicyNumber: originalPrimaryPolicyNumber,
            },
          },
        },
        'Error checking whether primary advisor is active'
      );
      return ClientReapplyResponseType.INACTIVE_PRIMARY_ADVISOR;
    }
    try {
      // If primary/servicing advisor is active, clone application
      await this.cloneApplicationForReapply();

      // Check issuePolicyRequestDate value to detect whether the policy has been inforced in the past
      if (!originalPrimaryPolicy.issuePolicyRequestDate) {
        return ClientReapplyResponseType.SUCCESS;
      }

      // Get the lastest application after cloneApplicationForReapply() gets called
      const newApplication = this.getLastestApplication();
      const newPrimaryPolicy: CoverpathPolicy = newApplication.primaryPolicy;

      // Call clonePolicyAgentInfo() to apply advisor's cloning logic on primary policy commission split
      await originalPrimaryPolicy?.clonePolicyAgentInfo(newPrimaryPolicy);

      if (originalApplication.isJointApp) {
        const originalSecondaryPolicy: CoverpathPolicy = originalApplication.secondaryPolicy;
        const newSecondaryPolicy: CoverpathPolicy = newApplication.secondaryPolicy;

        // Call clonePolicyAgentInfo() to apply advisor's cloning logic on secondary policy commission split
        await originalSecondaryPolicy?.clonePolicyAgentInfo(newSecondaryPolicy);
      }
      return ClientReapplyResponseType.SUCCESS;

      // Catch any failures except the failure from getProducerAgencyCorpFromCdm(), because it has already been catch in the previous code
    } catch (err) {
      const newApplication = this.getLastestApplication();
      const primaryPolicyNumber = newApplication.primaryPolicy.policyNumber;

      LoggerSingleton.getInstance().error(
        {
          module: 'AccountCenterController',
          function: 'cloneApplicationForReapply',
          category: Category.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
            data: {
              primaryPolicyNumber: primaryPolicyNumber,
            },
          },
        },
        'Error reapplying application'
      );
      return ClientReapplyResponseType.GENERIC_ERRORS;
    }
  }
}
